import { api } from "./api.ts";

const b64ToBytes = (b64: string): Uint8Array => {
  const pad = "=".repeat((4 - (b64.length % 4)) % 4);
  const raw = atob((b64 + pad).replace(/-/g, "+").replace(/_/g, "/"));
  return Uint8Array.from(raw, (c) => c.charCodeAt(0));
};

export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!("serviceWorker" in navigator)) return null;
  try {
    return await navigator.serviceWorker.register("/sw.js", { scope: "/" });
  } catch (e) {
    console.warn("service worker registration failed", e);
    return null;
  }
}

export type PushState = "unsupported" | "denied" | "granted" | "prompt";

export function pushState(): PushState {
  if (!("Notification" in window) || !("PushManager" in window)) return "unsupported";
  if (Notification.permission === "denied") return "denied";
  if (Notification.permission === "granted") return "granted";
  return "prompt";
}

/**
 * Asks for notification permission and registers the subscription.
 *
 * Called from a button, never on load: a permission prompt fired at a clinician
 * who has not yet worked out what the app is gets denied, and a denial is
 * sticky. Without push, page-com is a website you have to remember to check.
 */
export async function enablePush(vapidPublicKey: string): Promise<PushState> {
  if (pushState() === "unsupported") return "unsupported";
  const permission = await Notification.requestPermission();
  if (permission !== "granted") return permission === "denied" ? "denied" : "prompt";

  const reg = (await navigator.serviceWorker.ready) ?? (await registerServiceWorker());
  if (!reg) return "unsupported";
  if (!vapidPublicKey) {
    console.warn("no VAPID public key from the server; cannot subscribe");
    return "prompt";
  }
  const existing = await reg.pushManager.getSubscription();
  const sub =
    existing ??
    (await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: b64ToBytes(vapidPublicKey) as BufferSource,
    }));
  await api.subscribePush(sub.toJSON());
  return "granted";
}
